import {useMemo} from 'react';
import {AnalysisItem, PopulationCompare as IPopulationCompare} from '@/map/@types';
import {useComparePopulationByPoint, useComparePopulationByPolygon} from '@/map/api';
import {AreaOverlayType} from '@/map/enums';
import {ErrorItem} from '../../ErrorItem';
import {SectionBlock} from '../../StyledComponents';
import {FootTrafficCompare} from './FootTraffic';
import {HousePopulationTypeCompare} from './HousePopulationType';
import {OfficeWorkerCompare} from './OfficeWorker';
import {ResidentPopulationCompare} from './ResidentPopulation';
import {StudentsCompare} from './Students';

interface IProps {
  list: AnalysisItem[];
}

export const PopulationCompare = ({list}: IProps) => {
  const circles = useMemo(() => {
    return list.filter((i) => i.type === AreaOverlayType.CIRCLE);
  }, [list]);
  const polygons = useMemo(() => {
    return list.filter((i) => i.type === AreaOverlayType.POLYGON);
  }, [list]);

  const {queries: circleResults, refetchAll: refetchAllCircles} =
    useComparePopulationByPoint(circles);
  const {queries: polygonResults, refetchAll: refetchAllPolygons} =
    useComparePopulationByPolygon(polygons);

  const combinedResults = useMemo(() => {
    const results = [...(circleResults || {}), ...(polygonResults || {})];

    return list.map((i) => {
      const index =
        i.type === AreaOverlayType.CIRCLE
          ? circles.findIndex((j) => j.id === i.id)
          : polygons.findIndex((j) => j.id === i.id) + circles?.length;

      return results[index];
    });
  }, [circleResults, circles, list, polygonResults, polygons]);

  const combined = useMemo(() => {
    const data = combinedResults.map((result) => result?.data);

    return {
      data: (data.some((i) => !i) ? undefined : data) as
        | IPopulationCompare[]
        | undefined,
      isPending: combinedResults.some((result) => result.isLoading),
      error: combinedResults
        .map((result) => result.error)
        .filter((x) => !!x)?.[0],
    };
  }, [combinedResults]);

  const refetchAll = () => {
    refetchAllCircles();
    refetchAllPolygons();
  };

  if (combined.error) {
    return (
      <SectionBlock>
        <ErrorItem onRetry={refetchAll} />
      </SectionBlock>
    );
  }

  return (
    <>
      <HousePopulationTypeCompare {...combined} />
      <ResidentPopulationCompare {...combined} />
      <OfficeWorkerCompare {...combined} />
      <FootTrafficCompare {...combined} />
      <StudentsCompare {...combined} />
    </>
  );
};
